"use client";

import React from "react";
import { SectionContentProps } from "../../../types";
import { FooterContent, SocialLink, LinkGroup } from "../types";
import { SocialLinks, LinkGroupComponent, Copyright, getSocialIcon } from "../BaseFooter";
import { colors, footerStyles, layout, spacing } from "../tokens";

// ============================================================
// MODERN FOOTER - Contemporary with social prominence
// ============================================================
// Best for: Modern web apps, Developer tools, Open source projects
// Features: Newsletter focus, prominent social links, developer-focused
// ============================================================

interface ModernFooterProps extends SectionContentProps {
  content: FooterContent;
}

export const ModernFooter: React.FC<ModernFooterProps> = ({
  content: propContent,
  onContentChange,
  isSelected,
}) => {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Dynamic colors with fallbacks
  const backgroundColor = content.backgroundColor || "#020617"; // slate-950
  const textColor = content.textColor || "#ffffff";
  const accentColor = content.accentColor || "#3b82f6"; // blue-500

  const socialLinks: SocialLink[] = content.socialLinks || [
    { platform: "github", url: "#" },
    { platform: "twitter", url: "#" },
    { platform: "linkedin", url: "#" },
    { platform: "youtube", url: "#" },
    { platform: "discord", url: "#" },
  ];

  const defaultLinkGroups: LinkGroup[] = [
    {
      title: "Platform",
      links: [
        { label: "Overview", href: "#" },
        { label: "Features", href: "#" },
        { label: "Pricing", href: "#" },
        { label: "Roadmap", href: "#" },
      ],
    },
    {
      title: "Developers",
      links: [
        { label: "Documentation", href: "#" },
        { label: "API Reference", href: "#" },
        { label: "Integrations", href: "#" },
        { label: "CLI Tools", href: "#" },
      ],
    },
    {
      title: "Community",
      links: [
        { label: "Forum", href: "#" },
        { label: "Events", href: "#" },
        { label: "Blog", href: "#" },
        { label: "Showcase", href: "#" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy", href: "#" },
        { label: "Terms", href: "#" },
        { label: "Security", href: "#" },
        { label: "Status", href: "#" },
      ],
    },
  ];

  const linkGroups = content.linkGroups || defaultLinkGroups;

  return (
    <footer
      className={`${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        fontFamily,
        backgroundColor,
        color: textColor
      }}
      role="contentinfo"
      aria-label="Modern footer"
    >
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Newsletter Highlight Section */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 pb-12 border-b border-slate-800 mb-12">
          <div className="max-w-xl">
            {content.tagline && (
              <p className="text-blue-400 text-sm font-medium mb-2 tracking-wide uppercase">
                {content.tagline}
              </p>
            )}
            <h3 className="text-2xl font-bold mb-2">
              {content.companyName || "INSTACKS"}
            </h3>
            <p className="text-slate-400">
              {content.description || "The complete platform for modern web development."}
            </p>
          </div>

          {/* Newsletter Form */}
          <div className="w-full lg:w-auto">
            <form className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder={content.newsletterPlaceholder || "Enter your email"}
                className="px-5 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 min-w-[280px]"
                aria-label="Email for newsletter"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-all hover:scale-105"
              >
                Subscribe
              </button>
            </form>
            <p className="text-slate-500 text-xs mt-2">
              No spam, unsubscribe at any time.
            </p>
          </div>
        </div>

        {/* Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-16">
          {linkGroups.map((group, index) => (
            <div key={index}>
              <h4 className="text-white font-semibold mb-4">{group.title}</h4>
              <ul className="space-y-3" role="list">
                {group.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="text-slate-400 text-sm hover:text-blue-400 transition-colors"
                      target={link.isExternal ? "_blank" : undefined}
                      rel={link.isExternal ? "noopener noreferrer" : undefined}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-slate-800">
          {/* Copyright */}
          <div className="flex items-center gap-4">
            <Copyright
              text={content.copyright || "© 2026 INSTACKS. All rights reserved."}
              className="text-slate-500 text-sm"
            />
          </div>

          {/* Social Links - Prominent */}
          <div className="flex items-center gap-4">
            <span className="text-slate-500 text-sm">Follow us</span>
            <SocialLinks links={socialLinks} size="sm" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ModernFooter;
